import React, { useState, useEffect, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { FieldOfAction } from 'api-green-node';
import { ButtonGroup } from '@paygreen/paygreen-ui';
import { toast } from 'react-toastify';
import {
    CardClassic,
    ErrorBoundary,
    ActionBadge,
    CancelButton,
    ValidateButton,
} from '../../../components';
import { useAPIError, useUser } from '../../../context';
import { setCharityLocalStorage } from '../../../utils/authentication';
import CharityEditForm from './CharityEditForm';
import { putAssociation } from '../service';

const UserCharityInfo = ({
    t,
    charityDataLocal,
    isLoading: isPreLoading,
    setCharityListData,
    children,
}) => {
    const { addError } = useAPIError();
    const { setUser, user } = useUser();

    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // initializing all react-hook-form values and methods
    const defaultValues = {
        name: charityDataLocal?.name,
        description: charityDataLocal?.description,
        url: charityDataLocal?.url,
        fieldOfAction: t('charity.edit.fieldOfActionOptions')
            .indexOf(charityDataLocal?.fieldOfAction?.toUpperCase())
            .toString(),
    };

    const methods = useForm({ defaultValues });

    const { register, handleSubmit, reset, clearErrors } = methods;

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'name' });
        register({ name: 'description' });
        register({ name: 'url' });
        register({ name: 'fieldOfAction' });
    }, [register]);

    // Update selector default values in form when data is retrieved and form mounted
    useEffect(() => {
        reset({
            name: charityDataLocal?.name,
            url: charityDataLocal?.url,
            fieldOfAction: t('charity.edit.fieldOfActionOptions')
                .indexOf(charityDataLocal?.fieldOfAction?.toUpperCase())
                .toString(),
            description: charityDataLocal?.description,
        });
    }, [charityDataLocal, reset, t]);

    /**
     * PATCH request to API to update user's data
     * @returns {object} - object with user's data updated
     */
    const updateCharity = useCallback(
        (id, data) => {
            setIsLoading(true);

            putAssociation(id, data)
                .then((res) => {
                    setCharityListData([res.data]);

                    // if success we update local storage and user charity state to update alert message
                    setUser({
                        ...user,
                        isReadyToOnboard: true,
                    });

                    setCharityLocalStorage({
                        isReadyToOnboard: true,
                        isOnboardingCompleted: false,
                        isValidated: false,
                    });

                    setIsEditing(false);
                    !isLoading &&
                        toast(t('form.toastSuccess.charityAdminUpdate'));
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [
            addError,
            setIsLoading,
            setCharityListData,
            isLoading,
            t,
            setUser,
            user,
        ],
    );

    const submitForm = (data) => {
        const { name, description, url, fieldOfAction } = data;
        const { idAssociation, visible } = charityDataLocal;

        const updatedCharity = {
            idAssociation,
            name,
            description,
            url,
            fieldOfAction: FieldOfAction[fieldOfAction],
            visible,
        };

        updateCharity(charityDataLocal.idAssociation, updatedCharity);
    };

    const resetForm = () => {
        clearErrors();
        reset();
        setIsEditing(false);
    };

    return (
        <ErrorBoundary>
            <CardClassic
                title={t('charity.see.formTitle')}
                titleSize="md"
                isShadowWab
                blockWidth="md"
                logo={children}
            >
                <FormProvider {...methods}>
                    <CharityEditForm
                        defaultValues={defaultValues}
                        isLoading={isPreLoading || isLoading}
                        setIsEditing={setIsEditing}
                        onSubmit={handleSubmit(submitForm)}
                    >
                        {isEditing && (
                            <ButtonGroup marginTop="sm" marginBottom="sm">
                                <CancelButton
                                    buttonSize="md"
                                    onClick={resetForm}
                                    dataCy="charity-cancel"
                                >
                                    {t('form.button.cancel')}
                                </CancelButton>

                                <ValidateButton
                                    buttonSize="md"
                                    isDisabled={isLoading}
                                    dataCy="charity-validate"
                                >
                                    {t('resetPassword.validateButton')}
                                </ValidateButton>
                            </ButtonGroup>
                        )}
                    </CharityEditForm>
                </FormProvider>

                {!isEditing && (
                    <ButtonGroup marginTop="sm" marginBottom="sm">
                        <ActionBadge text={t('user.edit.badgetext')} />
                    </ButtonGroup>
                )}
            </CardClassic>
        </ErrorBoundary>
    );
};

export default withTranslation('common')(UserCharityInfo);
